import { PATIENT_POPULATIONS } from '@/modules/formulary/constants/patientPopulations';
import AutocompleteService from './autocomplete';
import { FACILITY_NAMES } from './__fixtures__/discoverFixtures';

// ReferenceService
//
// Cross-page lookup data that isn't medication-domain-specific: facility names
// and patient populations. Neither needs an endpoint of its own.
//
// `getFacilityNames` reuses the SAME tenant-scoped autocomplete subjects the
// Safety facility filter is driven by, exactly as MedicationService does for
// medications — so Formulary Management and the rest of the app resolve
// facilities from one source instead of two divergent lists. This supersedes
// the originally planned `GET /facilities`. See DISCOVER_DATA_CONTRACTS.md §1.
//
// `getPatientPopulations` resolves a real constant: a fixed two-value set with
// nothing serving it. It stays an async method returning the documented shape
// so swapping it for a real source later touches only this method — see
// constants/patientPopulations.js and §2.

// The autocomplete subjects backing the facility list.
//
// Formulary Management spans pump models, so it unions the per-model subjects
// rather than picking one — the same reasoning as
// MEDICATION_AUTOCOMPLETE_SUBJECTS in services/medication.js.
//
// Only Alaris and Baxter: bbraun and medfusion have no facility subject in
// filters/autocomplete.yaml because those models are not configured yet. When
// they are, add them here — a tenant running one would otherwise be missing
// its facilities entirely, which is silent rather than an error.
//
// NOT the untenanted `cf_facility_id`. That one resolves to the shared
// `bainbridge` Looker model rather than the tenant's pump data (the same trap
// documented for `drug_name` in services/medication.js), so it would look
// populated while disagreeing with every Safety dashboard.
//
// Note these subjects are named for the ID field, not the display name. They
// are the subjects Safety's `facility` filter uses, and that filter carries
// `autocomplete_mapped_field: facility_id` — see the unwrapping below.
const FACILITY_AUTOCOMPLETE_SUBJECTS = ['t_alaris::cf_facility_id', 't_baxter::facility_id'];

// One autocomplete row's display value.
//
// These subjects can come back either way, so both are handled rather than
// assumed:
//
//   'Hospital C'                    a flat string
//   { 'Hospital C': 'fac_17' }      a mapped name -> id pair
//
// The mapped shape is produced when a `mapped` param is present (see
// bbh/autocomplete/commands.py fetch_autocomplete_subject_results). We send no
// params, so a flat list is expected — but Safety's FilterBar tests each result
// for objecthood rather than trusting the subject, and the facility filter is
// declared WITH a mapped field, so the defensive read is the right one here.
//
// Takes the KEY of a mapped row, which is the display name; the value is the
// facility id, which nothing in this module shows or filters on.
const displayValue = (row) => {
  if (typeof row === 'string') return row;
  if (row && typeof row === 'object') {
    const [name] = Object.keys(row);
    return typeof name === 'string' ? name : '';
  }
  return '';
};

export default {
  // -> { facilities: string[] }
  //
  // The per-model requests are independent: one rejecting must not lose the
  // other's facilities, so each falls back to [] and the union is built from
  // whatever came back.
  //
  // De-duplication is CASE-INSENSITIVE, matching getAllMedications: a facility
  // present on both pump models appears in both lists, and the models need not
  // agree on casing. First spelling seen wins, and the result is sorted
  // case-insensitively so the order matches how the names are displayed.
  getFacilityNames() {
    const requests = FACILITY_AUTOCOMPLETE_SUBJECTS.map((subject) =>
      AutocompleteService.getAutocompleteResults(subject, {}).catch(() => [])
    );
    return Promise.all(requests).then((responses) => {
      const byLowerName = new Map();
      responses
        .filter(Array.isArray)
        .flat()
        .forEach((row) => {
          const name = displayValue(row);
          if (!name) return;
          const key = name.toLowerCase();
          if (!byLowerName.has(key)) byLowerName.set(key, name);
        });
      const facilities = [...byLowerName.values()].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

      // DEV/LOCAL ONLY, and for the same reason as the medication fallback: the
      // autocomplete service cannot run locally at all (it needs Firestore,
      // GCS, Looker and BigQuery — see autocomplete/tenant.py), so both
      // subjects come back empty and the Facility filter is unusable.
      //
      // Gated on the build-time flag rather than "did the request come back
      // empty", so a genuinely facility-less tenant in a deployed environment
      // shows NOTHING instead of plausible-looking fake hospital names. The
      // flag is false in every deployed build, so this branch and the fixture
      // import are stripped at build time.
      if (FIXTURE_FALLBACK_ENABLED && !facilities.length) {
        return { facilities: [...FACILITY_NAMES] };
      }

      return { facilities };
    });
  },

  // -> string[]
  //
  // A constant, not a request. Kept async and kept returning the contract's
  // shape so the swap to a real source is local to this method: return an
  // `AutocompleteService.getAutocompleteResults(<subject>, {})` call, the way
  // getFacilityNames above does.
  //
  // Copied rather than returned directly — the store commits the array it gets,
  // and handing out the module-level constant would let a consumer mutate the
  // shared value.
  getPatientPopulations() {
    return Promise.resolve([...PATIENT_POPULATIONS]);
  },
};
